/**
 * AI Skills Manager Page
 *
 * Purpose: Admin interface for managing AI Skills (name + markdown content)
 *          surfaced in the AI Agent Manager skills dropdown. Wraps SkeletonPage
 *          with a content header + the manager grid.
 * Architecture: Single-list page. Skills are GLOBAL-only, so there is no tenant
 *          selector / context loading (unlike AIAgentManagerPage).
 * Author: Development Team
 * Created: 2026-06-10
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Home as HomeIcon,
  PsychologyOutlined as SkillIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import SkeletonPage from '../../components/layout/SkeletonPage';
import AISkillsManager from '../../components/aiSkill/AISkillsManager';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';

const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const AISkillsManagerPage: React.FC = () => {
  const { can } = useAuth();
  // ai_skill.edit only counts in a GLOBAL membership, so it is absent in a client tenant
  const canEdit = can(PermissionKey.AI_SKILL_EDIT);

  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'AI Skills Manager', icon: <SkillIcon fontSize="inherit" /> },
  ];

  return (
    <SkeletonPage pageTitle="AI Skills Manager" breadcrumbs={breadcrumbs}>
      <ContentHeader>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0 }}>
            Manage skill definitions available to AI agents
          </Typography>
        </Box>
      </ContentHeader>

      <AISkillsManager canEdit={canEdit} />
    </SkeletonPage>
  );
};

export default AISkillsManagerPage;
